import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateAccount,
  useListAccounts,
  getListAccountsQueryKey,
  type Account,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";

/**
 * 2026-06-24 — Direct account creation (no lead required).
 *
 * The existing "Add from Lead" flow only converts closed-won leads. This
 * dialog creates an account from scratch as one of:
 *   • Individual — a single person (type=individual)
 *   • Team       — a company/team account (type=company); members are added
 *                  afterward on the account's Members tab
 *   • Hoblix     — the internal "house" account (type=company, isInternal=true)
 *                  for internal/office consumption; only one is allowed.
 */

type CreateKind = "individual" | "team" | "inhouse";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateAccountDialog({ open, onOpenChange }: Props) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createAccount = useCreateAccount();

  const [kind, setKind] = useState<CreateKind>("individual");
  const [name, setName] = useState("");

  // useListAccounts returns a raw Account[] (not an envelope). Used to block a
  // second Hoblix/internal account.
  const { data: accounts = [] } = useListAccounts(
    { pageSize: 200 },
    {
      query: {
        queryKey: ["/api/accounts", { forCreateDialog: true }],
        enabled: open,
      },
    },
  );
  const internalExists = useMemo(
    () => (accounts as Account[]).some((a) => a.isInternal),
    [accounts],
  );

  const isInhouse = kind === "inhouse";
  const effectiveName = name.trim();

  function reset() {
    setKind("individual");
    setName("");
  }

  async function handleSubmit() {
    if (!effectiveName) {
      toast({ title: "Enter a name.", variant: "destructive" });
      return;
    }
    try {
      const created = await createAccount.mutateAsync({
        data: {
          legalName: effectiveName,
          kind: "member",
          type: kind === "individual" ? "individual" : "company",
          isInternal: isInhouse,
          gstTreatment: "unregistered_b2c",
          placeOfSupply: "07",
        },
      });
      await queryClient.invalidateQueries({
        queryKey: getListAccountsQueryKey(),
      });
      toast({ title: `${effectiveName} created.` });
      onOpenChange(false);
      reset();
      const newId = (created as { id?: number })?.id;
      if (newId) navigate(`/accounts/${newId}`);
    } catch (e: unknown) {
      const s = (e as { status?: number })?.status;
      toast({
        title:
          s === 409
            ? "An account like this already exists."
            : "Failed to create account.",
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Account</DialogTitle>
          <DialogDescription>
            Create an account directly. Team members are added afterward on the
            account&rsquo;s Members tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <RadioGroup
              value={kind}
              onValueChange={(v) => {
                const k = v as CreateKind;
                setKind(k);
                // Prefill a sensible default name for the in-house account.
                if (k === "inhouse" && !name.trim()) setName("In-house");
              }}
              className="grid gap-2"
            >
              <Label className="flex cursor-pointer items-start gap-2 rounded-md border p-3 hover:bg-muted/50">
                <RadioGroupItem value="individual" className="mt-0.5" />
                <span className="space-y-0.5">
                  <span className="block text-sm font-medium">Individual</span>
                  <span className="block text-xs text-muted-foreground">
                    A single person.
                  </span>
                </span>
              </Label>
              <Label className="flex cursor-pointer items-start gap-2 rounded-md border p-3 hover:bg-muted/50">
                <RadioGroupItem value="team" className="mt-0.5" />
                <span className="space-y-0.5">
                  <span className="block text-sm font-medium">Team</span>
                  <span className="block text-xs text-muted-foreground">
                    A company/team account — add members afterward.
                  </span>
                </span>
              </Label>
              <Label
                className={`flex items-start gap-2 rounded-md border p-3 ${
                  internalExists
                    ? "opacity-50"
                    : "cursor-pointer hover:bg-muted/50"
                }`}
              >
                <RadioGroupItem
                  value="inhouse"
                  className="mt-0.5"
                  disabled={internalExists}
                />
                <span className="space-y-0.5">
                  <span className="block text-sm font-medium">
                    In-house account
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {internalExists
                      ? "Already exists (only one in-house account)."
                      : "Internal/office consumption (F&B etc.) — no membership plan."}
                  </span>
                </span>
              </Label>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>
              {kind === "team"
                ? "Team / company name"
                : kind === "inhouse"
                  ? "In-house account name"
                  : "Full name"}
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                kind === "team"
                  ? "e.g. Acme Pvt Ltd"
                  : kind === "inhouse"
                    ? "e.g. Hoblix In-house"
                    : "e.g. Rohan Sharma"
              }
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createAccount.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createAccount.isPending || !effectiveName}
          >
            {createAccount.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
